import { useCallback, useMemo } from "react";
import { generatePath, Link } from "react-router";

import type { BillingStatus, TicketListAvailable_BillingPageQuery } from "@/lib/gql/__generated__/graphql";
import { CurrencyType } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants/external-link";
import { PATHS } from "@/routes/paths";

import type {
	BillingSettingDialogStateType,
	BillingSettingDialogType,
	OnUpdatePurchaseBasicBillingTicketType,
} from "@/components/features/settings/billing/model/use-case";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";

import { PurchaseTicketDialog } from "../../../_shared/dialog";
import { SettingsBillingSection } from "../../../_shared/section";
import { useTicketList } from "../../../_shared/ticket-list/ticket-list";

import type { TicketListItemType } from "../../../_shared/ticket-list/ticket-list";
import type { ComponentProps } from "react";

/**
 * CHILLNN サポート「基本料金の複数施設割引について」
 * https://support.chillnn.com/faq/host/other/3cafffe15c5781afa505f5465b5c1c81
 *
 * ベースURLは externalLink.faqSite（dev/prd で自動切替）を使う。
 * パスは Notion ページ ID で構成されるため、日本語タイトルや旧スラッグは使わない。
 */
const MULTIPLE_HOTEL_DISCOUNT_FAQ_URL = `${externalLink.faqSite}/faq/host/other/3cafffe15c5781afa505f5465b5c1c81`;

type Props = {
	hotelID: string;

	ticketBasicListAvailable: TicketListAvailable_BillingPageQuery["ticketBasicListAvailable"];

	billingStatus: BillingStatus;
	onPurchaseBasicBillingTicket: OnUpdatePurchaseBasicBillingTicketType;

	openBillingSettingDialog: BillingSettingDialogStateType;
	handleOpenChange: (type: BillingSettingDialogType) => (callBackFunc: NodeJS.Timeout) => (open: boolean) => void;
	activateBillingSettingDialog: (type: BillingSettingDialogType) => void;
	deactivateBillingSettingDialog: (type: BillingSettingDialogType) => void;
};

export const BasicTicketPurchaseTicketSection = ({
	hotelID,
	ticketBasicListAvailable,
	billingStatus,
	onPurchaseBasicBillingTicket,

	openBillingSettingDialog,
	handleOpenChange: _handleOpenChange,
	activateBillingSettingDialog,
	deactivateBillingSettingDialog,
}: Props) => {
	// 定期購入しているチケットのID
	const defaultRecurringProBillingTicketID = !billingStatus.isStopRecurringBasicBilling
		? (billingStatus.defaultBasicBillingTicketID ?? undefined)
		: undefined;

	// チケットのrender hooks を初期化（チケットのリストコンポーネントと選択されているチケットIDを返す）
	const { selectedTicketID, ticketList_Component, handleChangeSelectedTicketID } = useTicketList({
		accentColor: "yellow",
		defaultTicketID: defaultRecurringProBillingTicketID,
		ticketList: ticketBasicListAvailable.tickets.map((ticket) => {
			return {
				id: ticket?.hotelBasicBillingTicketID ?? "",
				name: ticket?.name || "",
				validMonth: ticket?.validMonth || 0,
				price: ticket?.price || { amount: 0, currencyType: CurrencyType.Jpy },
				isPersonal: ticket?.isPersonal ?? false,
			} satisfies TicketListItemType;
		}),
	});

	// 返却されているチケットが全て個別チケットかどうかを判定
	const isPersonalTicket = useMemo(() => {
		const tickets = ticketBasicListAvailable.tickets;
		if (!tickets || tickets.length === 0) return false;
		return tickets.every((ticket) => ticket?.isPersonal === true);
	}, [ticketBasicListAvailable.tickets]);

	const handleOpenChange = useCallback(
		(open: boolean) =>
			_handleOpenChange("BASIC")(
				setTimeout(() => handleChangeSelectedTicketID(defaultRecurringProBillingTicketID ?? ""), 100),
			)(open),
		[_handleOpenChange, defaultRecurringProBillingTicketID, handleChangeSelectedTicketID],
	);

	const handleClick = useCallback(() => {
		activateBillingSettingDialog("BASIC");
	}, [activateBillingSettingDialog]);

	// 購入できるのかどうか
	const isValid = !!selectedTicketID;
	// ボタンのvariant
	const variant: ComponentProps<typeof Button>["variant"] = isValid ? "default" : "disabled";

	// チケット購入ボタンのクリックハンドラー
	const handleClickConversionButton = useCallback(async () => {
		if (selectedTicketID) {
			deactivateBillingSettingDialog("BASIC");

			const result = await onPurchaseBasicBillingTicket({
				defaultBasicBillingTicketID: selectedTicketID,
			});

			if (result) {
				notifyUserEventInfo({
					title: "基本料金のチケットを購入しました",
				});
			}
		}
	}, [deactivateBillingSettingDialog, onPurchaseBasicBillingTicket, selectedTicketID]);

	return (
		<SettingsBillingSection title="購入予定のチケット">
			<p className="col-span-2">
				{billingStatus.defaultBasicBillingTicketName || "購入予定のチケットはありません"}
			</p>
			<div className="col-span-2 flex justify-end">
				<Button type="button" variant="outline" onClick={handleClick}>
					設定する
				</Button>
			</div>

			<PurchaseTicketDialog
				title="基本料金"
				open={openBillingSettingDialog.BASIC}
				handleOpenChange={handleOpenChange}
			>
				<div className="space-y-8">
					<div className="space-y-1 text-sm">
						<p>基本料金は、CHILLNNの基本サービスを利用するためにかかる料金です。</p>
						{isPersonalTicket ? (
							<>
								<p>
									契約期間終了の「7日前」にご登録いただいたクレジットカードから自動的に引き落とされます。
								</p>
								<p>
									料金プランの変更は
									{hotelID ? (
										<Link
											to={generatePath(PATHS.HOTEL.CONTACT.NEW, { hotelID })}
											className="text-link-400 underline"
											viewTransition
										>
											こちら
										</Link>
									) : (
										<span className="text-link-400 underline">こちら</span>
									)}
									からお問い合わせください。
								</p>
							</>
						) : (
							<>
								<p className="text-brand">チケット初回購入時に1ヶ月分の無料期間が追加されます。</p>
								<p>
									基本料金は契約期間終了の「7日前」にご登録いただいたクレジットカードから自動的に引き落とされます。
								</p>
								<p>料金プランの変更はいつでも可能です。</p>
							</>
						)}
						<p>
							複数施設でご利用の場合、年額基本料金の割引が適用されます。詳細は
							<a
								href={MULTIPLE_HOTEL_DISCOUNT_FAQ_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="text-link-400 underline"
							>
								こちら
							</a>
						</p>
					</div>

					{ticketList_Component()}

					<div className="flex justify-end">
						<Button variant={variant} size="xl" onClick={handleClickConversionButton}>
							購入
						</Button>
					</div>
				</div>
			</PurchaseTicketDialog>
		</SettingsBillingSection>
	);
};

BasicTicketPurchaseTicketSection.displayName = "BasicTicketPurchaseTicketSection";
